/**
 * Numu quantity bundle
 * -------------------------------------------------------------------------
 * Replaces the Fly Bundles & Upsells volume WIDGET with in-theme markup.
 * The app's JavaScript is disabled (its theme app embed is off in this theme),
 * which is what fixed the cart drawer upsell bug — flyBundlesCore.min.js was
 * monkey-patching window.fetch and rewriting every /cart/add payload to the
 * product being viewed.
 *
 * WHAT STILL DEPENDS ON THE APP
 * -----------------------------
 * The discount itself. Selecting a tier tags the cart line with the app's own
 * line-item properties, and the app's "Buy More, Save More" Shopify Function
 * applies the real 10% / 15% server-side.
 *
 * This is not the architecture we wanted — a custom function of our own would
 * have removed the dependency entirely — but Shopify only allows Functions
 * from a CUSTOM app on Shopify Plus. NaliaCPH is on Basic. Fly Bundles is a
 * published public app, which is the only reason its function is allowed to
 * run here.
 *
 * Consequences to know:
 *   - Fly Bundles must stay INSTALLED. Uninstalling it silently removes the
 *     discount while this widget keeps promising it.
 *   - Its theme app embed must stay OFF, or the cart drawer bug returns.
 *   - The property names below are a private, undocumented contract with that
 *     app. If a future app update changes them the discount stops. Re-verify
 *     after any Fly Bundles update: add Køb 2 and confirm the cart charges
 *     405,00 kr on a 225,00 kr product.
 *
 * Verified behaviour (tested against the live function):
 *   Køb 2 -> 10%, Køb 3 -> 15%, and the tier follows the LIVE line quantity,
 *   so editing quantity in the cart drawer re-tiers correctly and dropping to
 *   1 removes the discount. A line without these properties never gets a
 *   discount, which is what keeps the offer bundle-only.
 *
 * Every price shown is rendered by Liquid (see numu-bundle.liquid) for each
 * variant/tier pair, so the money format matches the theme and no money maths
 * happens in JS.
 */
if (!customElements.get('numu-bundle')) {
  customElements.define(
    'numu-bundle',
    class NumuBundle extends HTMLElement {
      // The app's offer id for this store. Its bundle is configured as
      // ALL_PRODUCTS, so one offer id covers every product page.
      static OFFER_ID = 'VOL-470330';

      connectedCallback() {
        this.variants = this.parseConfig();
        this.tiers = Array.from(this.querySelectorAll('[data-numu-tier]'));
        if (!this.tiers.length) return;

        this.form = document.getElementById(this.dataset.formId);
        this.variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        this.quantityInput = this.resolveQuantityInput();
        this.markerInputs = this.ensureMarkerInputs();

        this.onTierChange = this.handleTierChange.bind(this);
        this.onVariantInputChange = this.syncToVariant.bind(this, undefined);

        this.addEventListener('change', this.onTierChange);
        if (this.variantInput) this.variantInput.addEventListener('change', this.onVariantInputChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            const data = event && event.data ? event.data : {};
            if (data.sectionId && this.dataset.sectionId && data.sectionId !== this.dataset.sectionId) return;
            this.syncToVariant(data.variant ? data.variant.id : undefined);
          });
        }

        this.syncToVariant();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onTierChange);
        if (this.variantInput) this.variantInput.removeEventListener('change', this.onVariantInputChange);
        if (this.unsubscribeVariantChange) this.unsubscribeVariantChange();
      }

      parseConfig() {
        const node = this.querySelector('[data-numu-bundle-variants]');
        try {
          return JSON.parse((node && node.textContent) || '{}');
        } catch (error) {
          console.error('[numu-bundle] could not parse variant data', error);
          return {};
        }
      }

      resolveQuantityInput() {
        if (!this.form) return null;
        const themeInput = this.form.querySelector('.quantity__input');
        const hiddenInput = this.form.querySelector('[data-numu-bundle-quantity]');
        if (themeInput) {
          if (hiddenInput) hiddenInput.remove();
          return themeInput;
        }
        return hiddenInput;
      }

      /**
       * The three line-item properties the discount function looks for.
       * Created in JS rather than Liquid so that if this script fails to load,
       * the form stays a plain quantity-1 add to cart and can never claim a
       * discount it won't get. Kept disabled until a multi-buy tier is chosen —
       * disabled controls aren't submitted, so Køb 1 sends nothing.
       */
      ensureMarkerInputs() {
        if (!this.form) return [];
        const wanted = [
          ['_flyBundles:OfferId', 'offerId'],
          ['_flyBundles:prodQty', 'prodQty'],
          ['_flyBundles:selectedTier', 'selectedTier'],
        ];
        return wanted.map(([propertyName, key]) => {
          let input = this.form.querySelector('[data-numu-marker="' + key + '"]');
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = 'properties[' + propertyName + ']';
            input.value = '';
            input.disabled = true;
            input.setAttribute('data-numu-marker', key);
            this.form.appendChild(input);
          }
          return input;
        });
      }

      get selectedTier() {
        return (
          this.tiers.find((tier) => {
            const radio = tier.querySelector('input[type="radio"]');
            return radio && radio.checked;
          }) || null
        );
      }

      handleTierChange(event) {
        if (!event.target.matches('[data-numu-tier] input[type="radio"]')) return;
        this.paintSelection();
        this.pushQuantityToForm();
      }

      paintSelection() {
        this.tiers.forEach((tier) => {
          const radio = tier.querySelector('input[type="radio"]');
          tier.classList.toggle('is-selected', !!(radio && radio.checked));
        });
      }

      pushQuantityToForm() {
        const tier = this.selectedTier;
        const quantity = tier ? tier.dataset.qty : '1';
        const tierIndex = tier ? this.tiers.indexOf(tier) : 0;
        const isBundle = parseInt(quantity, 10) > 1;

        if (this.quantityInput && this.quantityInput.value !== quantity) {
          this.quantityInput.value = quantity;
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        const values = {
          offerId: NumuBundle.OFFER_ID,
          prodQty: String(quantity),
          selectedTier: String(tierIndex),
        };

        this.markerInputs.forEach((input) => {
          const key = input.getAttribute('data-numu-marker');
          input.value = values[key] || '';
          input.disabled = !isBundle;
        });
      }

      syncToVariant(variantIdFromEvent) {
        const variantId = String(variantIdFromEvent || (this.variantInput && this.variantInput.value) || '');
        const variant = this.variants[variantId];

        this.classList.toggle('is-unavailable', !!variantId && !(variant && variant.available));

        this.tiers.forEach((tier, index) => {
          const priceEl = tier.querySelector('[data-numu-price]');
          const compareEl = tier.querySelector('[data-numu-compare]');
          const radio = tier.querySelector('input[type="radio"]');
          const quantity = parseInt(tier.dataset.qty, 10) || 1;
          const tierData = variant && variant.tiers ? variant.tiers[index] : null;

          if (tierData) {
            if (priceEl) priceEl.textContent = tierData.price;
            if (compareEl) compareEl.textContent = tierData.compare || '';
          }

          const selectable = !!variant && variant.available && quantity <= variant.max;
          tier.classList.toggle('is-disabled', !selectable);
          if (radio) {
            radio.disabled = !selectable;
            radio.setAttribute('aria-disabled', String(!selectable));
          }
        });

        const current = this.selectedTier;
        if (!current || current.classList.contains('is-disabled')) {
          const fallback = this.tiers
            .slice()
            .reverse()
            .find((tier) => !tier.classList.contains('is-disabled'));
          const radio = fallback ? fallback.querySelector('input[type="radio"]') : null;
          if (radio) radio.checked = true;
        }

        this.paintSelection();
        this.pushQuantityToForm();
      }
    }
  );
}